import { BarChart } from '@mui/x-charts';
import React, { useMemo } from 'react';
import { DeviceAccessCount } from 'v2/hooks/useWeeklyAccessEvents/useWeeklyAccessEvents';
import ChartCard, { FULL_CHART_HEIGHT } from 'v2/pages/Dashboard/charts/ChartCard';

const BAR_BORDER_RADIUS = 4;
// keeps bars thin instead of filling the whole band
const CATEGORY_GAP_RATIO = 0.5;
// room for a device name; anything longer is ellipsized, and the tooltip still spells it out in full
const Y_AXIS_WIDTH = 88;
// the axis sizes already reserve room for the labels, so left/bottom need no extra margin
const CHART_MARGIN = { top: 8, right: 8, bottom: 0, left: 0 };

interface Props {
  /** Devices in ranking order, highest count first — the chart does not reorder them. */
  devices: DeviceAccessCount[];
  /** Bar colour, so each card can tell its own metric apart at a glance. */
  color: string;
  title: string;
  subtitle: string;
  emptyStateContent: string;
  isLoading: boolean;
  hasError: boolean;
}

/** Horizontal bar chart card ranking devices by an access-event count. */
const RankedDevicesChart: React.FC<Props> = ({
  devices,
  color,
  title,
  subtitle,
  emptyStateContent,
  isLoading,
  hasError
}) => {
  const deviceNamesById = useMemo(
    () =>
      devices.reduce<Record<string, string>>(
        (names, { deviceId, deviceName }) => ({ ...names, [deviceId]: deviceName }),
        {}
      ),
    [devices]
  );

  return (
    <ChartCard
      title={title}
      subtitle={subtitle}
      isLoading={isLoading}
      hasError={hasError}
      isEmpty={devices.length === 0}
      emptyStateContent={emptyStateContent}
    >
      <BarChart
        // no legend of its own, so the plot gets the card's whole chart area
        height={FULL_CHART_HEIGHT}
        margin={CHART_MARGIN}
        layout="horizontal"
        series={[
          {
            data: devices.map(({ count }) => count),
            color,
            label: title
          }
        ]}
        yAxis={[
          {
            scaleType: 'band',
            // Keyed by device id, not name: a band axis treats its value as the category identity, so
            // two devices sharing a name would collapse into one band — the bars land on top of each
            // other and hovering one highlights the other. Ids are unique, and the formatter below
            // puts the name back on the tick and in the tooltip.
            //
            // The caller sorts highest first, and a horizontal band axis renders the first entry at
            // the top, so the ranking needs no further reordering here.
            data: devices.map(({ deviceId }) => deviceId),
            valueFormatter: (deviceId: string) => deviceNamesById[deviceId] ?? deviceId,
            width: Y_AXIS_WIDTH,
            categoryGapRatio: CATEGORY_GAP_RATIO,
            disableTicks: true
          }
        ]}
        // no min/max on purpose: letting the domain follow the data is what makes an outlier device
        // rescale the axis instead of running off the end of the plot
        xAxis={[{ disableTicks: true }]}
        grid={{ vertical: true }}
        borderRadius={BAR_BORDER_RADIUS}
        hideLegend
      />
    </ChartCard>
  );
};

export default RankedDevicesChart;
